/**
 * In-process token cache for GitHub App installation tokens.
 *
 * Lives in the importing process — one cache per Container App replica
 * (gateway) and one per Claude Code session (local MCP plugin). Acceptable
 * because installation tokens rotate every ~50 min (60 min TTL − 10 min
 * safety margin); cold-start cost is one round-trip; replica/session
 * isolation is a tolerable tradeoff for no shared-state infra (DESIGN §10.2).
 */

export const EXPIRY_SAFETY_MS = 10 * 60 * 1000; // refresh if <10 min remaining

interface Entry { token: string; expiresAt: Date }
const cache = new Map<string, Entry>();

function key(installationId: string, repository: string): string {
  return `${installationId}:${repository}`;
}

/** TEST-ONLY: clear the cache between tests. */
export function _resetTokenCache(): void {
  cache.clear();
}

/**
 * Look up a cached token. Returns null if missing, expired, or within the
 * safety margin (caller must mint fresh).
 */
export function getCachedToken(
  installationId: string,
  repository: string,
): Entry | null {
  const e = cache.get(key(installationId, repository));
  if (!e) return null;
  if (e.expiresAt.getTime() - Date.now() <= EXPIRY_SAFETY_MS) return null;
  return e;
}

/** Cache a freshly-minted token. */
export function putCachedToken(
  installationId: string,
  repository: string,
  token: string,
  expiresAt: Date,
): void {
  cache.set(key(installationId, repository), { token, expiresAt });
}
